import {useEffect, useState} from "react";
import {Button, Form, Input, Select, Alert, Switch} from "antd";
import {useTranslation} from "react-i18next";
import ApiService from "../../core/service/api-service";
import moment from "moment";
import BirthdateInput from "../../core/components/BirthdateInput";
import OperationProgress, {useOperationProgress} from "../../core/components/OperationProgress";

export default function PatientEditModal({studyRow, onSaved}) {
    const {t} = useTranslation();
    const [form] = Form.useForm();
    const [saving, setSaving] = useState(false);
    const [allStudies, setAllStudies] = useState(false);
    const {operationId, progress, start, fail} = useOperationProgress();

    useEffect(() => {
        if (studyRow) {
            // BirthdateInput 'YYYY-MM-DD' bekler; grid'den gelen değer ISO tarih ya da tarih-saat olabilir.
            const birthdateStr = studyRow.birthdate
                ? moment(studyRow.birthdate).format('YYYY-MM-DD')
                : null;
            form.setFieldsValue({
                dicomPatientId: studyRow.dicomPatientId || '',
                name: studyRow.patientName || '',
                birthdate: birthdateStr,
                gender: studyRow.patientSex || undefined
            });
        }
    }, [studyRow, form]);

    const handleSubmit = async (values) => {
        setSaving(true);
        try {
            // BirthdateInput 'YYYY-MM-DD' verir; DICOM tarafı YYYYMMDD ister.
            const birthdateStr = values.birthdate
                ? values.birthdate.replace(/-/g, '')
                : null;

            const data = {
                patientId: studyRow.patientId,
                name: values.name,
                dicomPatientId: values.dicomPatientId,
                birthdate: birthdateStr,
                gender: values.gender || null,
                allStudies: allStudies,
                studyId: studyRow.id,
                operationId: start()
            };

            const response = await ApiService.Patient.UpdateAndCopyStudies(data);
            if (response.success) {
                onSaved && onSaved();
            } else {
                // Sunucu hatasında ilerleme olayı gelmemiş olabilir; paneli biz kapatırız.
                fail(response.message || t('Operation failed'));
            }
        } finally {
            setSaving(false);
        }
    };

    return (
        <div style={{minWidth: 400}}>
            <Alert
                message={t('Warning')}
                description={t('This will update patient information and copy all studies of this patient with new patient info in PACS.')}
                type="warning"
                showIcon
                style={{marginBottom: 16}}
            />

            <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
            >
                <Form.Item
                    name="dicomPatientId"
                    label={t('Patient ID')}
                    rules={[{required: true, message: t('Please enter patient ID')}]}
                >
                    <Input placeholder={t('Patient ID')} />
                </Form.Item>

                <Form.Item
                    name="name"
                    label={t('Patient Name')}
                    rules={[{required: true, message: t('Please enter patient name')}]}
                >
                    <Input placeholder={t('Patient Name')} />
                </Form.Item>

                <Form.Item
                    name="birthdate"
                    label={t('Birth Date')}
                >
                    <BirthdateInput disabled={saving} />
                </Form.Item>

                <Form.Item
                    name="gender"
                    label={t('Gender')}
                >
                    <Select placeholder={t('Select gender')} allowClear>
                        <Select.Option value="M">{t('Male')}</Select.Option>
                        <Select.Option value="F">{t('Female')}</Select.Option>
                        <Select.Option value="O">{t('Other')}</Select.Option>
                    </Select>
                </Form.Item>

                <Form.Item label={t('All Studies')}>
                    <Switch
                        checked={allStudies}
                        onChange={setAllStudies}
                        disabled={saving}
                    />
                </Form.Item>

                {operationId && (
                    <div style={{marginBottom: 16}}>
                        <OperationProgress progress={progress} title={t('Updating patient')} />
                    </div>
                )}

                <Form.Item>
                    <Button type="primary" htmlType="submit" loading={saving}>
                        {t('Update Patient')}
                    </Button>
                </Form.Item>
            </Form>
        </div>
    );
}
